'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

export type SeedRole = 'engineer' | 'teacher' | 'scientist' | 'farmer' | 'native' | 'returning-citizen';

interface RoleConfig {
  id: SeedRole;
  label: string;
  arabic: string;
  icon: string;
  lineageColor: string;
  biomeColor: string;
  tagline: string;
  // Role-context module set
  modules: { id: string; label: string; desc: string; href: string; color: string }[];
  // Amanah tracker categories
  amanahCategories: { id: string; label: string; desc: string; weight: number; color: string }[];
  // Alhamdulillah weight categories
  alhamdulilahWeights: { category: string; weight: number; desc: string }[];
  // Keys engagement
  primaryKey: string;
  secondaryKey: string;
  // IRL entry sequence
  irlSequence: string;
  // MRG domain
  mrgDomain: string;
}

// ─── ROLE CONFIGURATIONS ──────────────────────────────────────────────────────

const ROLE_CONFIGS: Record<SeedRole, RoleConfig> = {
  engineer: {
    id: 'engineer',
    label: 'Engineer',
    arabic: 'مُهَنْدِس',
    icon: '⚙',
    lineageColor: '#2d8a87',
    biomeColor: '#2d7a47',
    tagline: 'Systems, provision, and the architecture of creation',
    modules: [
      { id: 'systems-lens', label: 'Systems Lens', desc: 'Apply engineering methodology to creation\'s infrastructure', href: '/inner-reality-lens', color: '#2d8a87' },
      { id: 'mizan-audit', label: 'Mizan Audit', desc: 'Assess balance in engineered systems', href: '/keys-to-kulli-haal', color: '#D4A82A' },
      { id: 'provision-map', label: 'Provision Map', desc: 'Map Ar-Razzaq\'s provision through infrastructure', href: '/muslim-resource-guide', color: '#2d7a47' },
      { id: 'taqdir-measurement', label: 'Taqdir Measurement', desc: 'Document the measurements given to systems', href: '/inner-reality-lens', color: '#c9a84c' },
    ],
    amanahCategories: [
      { id: 'infrastructure', label: 'Infrastructure Amanah', desc: 'Systems that serve the community', weight: 3, color: '#2d8a87' },
      { id: 'provision', label: 'Provision Amanah', desc: 'Ensuring resources reach those in need', weight: 2, color: '#2d7a47' },
      { id: 'mizan', label: 'Mizan Amanah', desc: 'Maintaining balance in built systems', weight: 2, color: '#D4A82A' },
    ],
    alhamdulilahWeights: [
      { category: 'Systems Recognition', weight: 3, desc: 'Recognizing Al-Qadir in engineered systems' },
      { category: 'Provision Bridge', weight: 5, desc: 'Connecting a community member to infrastructure' },
      { category: 'Mizan Restoration', weight: 7, desc: 'Restoring balance in a broken system' },
    ],
    primaryKey: 'Key 04: Mizan — The Balance',
    secondaryKey: 'Key 05: Amanah — The Trust',
    irlSequence: 'S4: Mycorrhizal Network (hidden infrastructure)',
    mrgDomain: 'Infrastructure & Housing',
  },
  teacher: {
    id: 'teacher',
    label: 'Teacher',
    arabic: 'مُعَلِّم',
    icon: '✦',
    lineageColor: '#8a8ab8',
    biomeColor: '#2d7a47',
    tagline: 'Curriculum, transmission, and the Ilm that flows',
    modules: [
      { id: 'curriculum-lens', label: 'Curriculum Lens', desc: 'Creation as the ultimate curriculum', href: '/inner-reality-lens', color: '#8a8ab8' },
      { id: 'yaqeen-pedagogy', label: 'Yaqeen Pedagogy', desc: 'Ilm → Ayn → Haqq as teaching stages', href: '/keys-to-kulli-haal', color: '#c9a84c' },
      { id: 'transmission-chain', label: 'Transmission Chain', desc: 'Isnad of knowledge in your community', href: '/observation-chain-archive', color: '#2d8a87' },
      { id: 'light-sequence', label: 'Light Sequence (S3)', desc: 'Canopy photosynthesis as the first teacher', href: '/inner-reality-lens', color: '#D4A82A' },
    ],
    amanahCategories: [
      { id: 'knowledge', label: 'Knowledge Amanah', desc: 'Transmitting beneficial Ilm', weight: 3, color: '#8a8ab8' },
      { id: 'youth', label: 'Youth Amanah', desc: 'Nurturing the next generation', weight: 3, color: '#2d7a47' },
      { id: 'curriculum', label: 'Curriculum Amanah', desc: 'Orienting learning toward recognition', weight: 2, color: '#c9a84c' },
    ],
    alhamdulilahWeights: [
      { category: 'Knowledge Transmission', weight: 3, desc: 'Sharing beneficial Ilm with another' },
      { category: 'Yaqeen Deepening', weight: 5, desc: 'Helping someone move from Ilm to Ayn' },
      { category: 'Haqq Facilitation', weight: 7, desc: 'Guiding someone to direct experience' },
    ],
    primaryKey: 'Key 01: Ayat — Signs in Creation',
    secondaryKey: 'Key 07: Yaqeen — Certainty',
    irlSequence: 'S3: Canopy Photosynthesis (light as teacher)',
    mrgDomain: 'Education & Literacy',
  },
  scientist: {
    id: 'scientist',
    label: 'Scientist',
    arabic: 'عَالِم',
    icon: '◎',
    lineageColor: '#2d7a47',
    biomeColor: '#2d7a47',
    tagline: 'Research, observation, and the Taqdir of measurement',
    modules: [
      { id: 'research-lens', label: 'Research Lens', desc: 'Scientific method as Tafakkur', href: '/inner-reality-lens', color: '#2d7a47' },
      { id: 'taqdir-lab', label: 'Taqdir Lab', desc: 'Documenting divine measurements in creation', href: '/inner-reality-lens', color: '#c9a84c' },
      { id: 'isnad-verification', label: 'Isnad Verification', desc: 'Independent corroboration of observations', href: '/observation-chain-archive', color: '#2d8a87' },
      { id: 'names-extraction', label: 'Names Extraction', desc: 'Identifying Names in scientific processes', href: '/names-reference', color: '#8a8ab8' },
    ],
    amanahCategories: [
      { id: 'observation', label: 'Observation Amanah', desc: 'Accurate documentation of creation', weight: 3, color: '#2d7a47' },
      { id: 'verification', label: 'Verification Amanah', desc: 'Corroborating community observations', weight: 2, color: '#2d8a87' },
      { id: 'sharing', label: 'Sharing Amanah', desc: 'Making research accessible', weight: 2, color: '#c9a84c' },
    ],
    alhamdulilahWeights: [
      { category: 'Observation Submitted', weight: 3, desc: 'Documenting a Taqdir measurement' },
      { category: 'Verification Complete', weight: 4, desc: 'Corroborating another\'s observation' },
      { category: 'Research Bridged', weight: 6, desc: 'Connecting research to community benefit' },
    ],
    primaryKey: 'Key 02: Haal — States of Being',
    secondaryKey: 'Key 04: Mizan — The Balance',
    irlSequence: 'S5: Amphibian Indicator (environmental measurement)',
    mrgDomain: 'Environmental Stewardship',
  },
  farmer: {
    id: 'farmer',
    label: 'Farmer / Gardener',
    arabic: 'فَلَّاح',
    icon: '⬡',
    lineageColor: '#D4A82A',
    biomeColor: '#2d7a47',
    tagline: 'Soil, growth, and the Sunnat of cultivation',
    modules: [
      { id: 'soil-lens', label: 'Soil Lens', desc: 'Soil as the Keys — nutrients as framework', href: '/inner-reality-lens', color: '#D4A82A' },
      { id: 'growth-cycle', label: 'Growth Cycle', desc: 'Seasonal Sunnat of cultivation', href: '/inner-reality-lens', color: '#2d7a47' },
      { id: 'provision-garden', label: 'Provision Garden', desc: 'Ar-Razzaq\'s provision through cultivation', href: '/muslim-resource-guide', color: '#2d7a47' },
      { id: 'quran-27-60', label: 'Quran 27:60', desc: '"Not a tree was it for you to cause to grow"', href: '/keys-to-kulli-haal', color: '#c9a84c' },
    ],
    amanahCategories: [
      { id: 'soil', label: 'Soil Amanah', desc: 'Tending the earth as a trust', weight: 3, color: '#D4A82A' },
      { id: 'food', label: 'Food Amanah', desc: 'Ensuring community access to provision', weight: 3, color: '#2d7a47' },
      { id: 'cycle', label: 'Cycle Amanah', desc: 'Honoring the seasonal Sunnat', weight: 2, color: '#c9a84c' },
    ],
    alhamdulilahWeights: [
      { category: 'Soil Observation', weight: 3, desc: 'Documenting soil health as Ayah' },
      { category: 'Provision Shared', weight: 5, desc: 'Sharing harvest with community' },
      { category: 'Cycle Completed', weight: 7, desc: 'Full seasonal cycle documented' },
    ],
    primaryKey: 'Key 03: Taqwah — Consciousness',
    secondaryKey: 'Key 06: Falah — Flourishing',
    irlSequence: 'S1: Seasonal Cycle (growth and dormancy)',
    mrgDomain: 'Food & Nutrition',
  },
  native: {
    id: 'native',
    label: 'Native / Indigenous',
    arabic: 'أَصِيل',
    icon: '◈',
    lineageColor: '#c9a84c',
    biomeColor: '#2d7a47',
    tagline: 'Land stewardship, ancestral knowledge, and Amanah',
    modules: [
      { id: 'land-lens', label: 'Land Lens', desc: 'Ancestral knowledge as Ilm Yaqeen', href: '/inner-reality-lens', color: '#c9a84c' },
      { id: 'stewardship-map', label: 'Stewardship Map', desc: 'Mapping Amanah across the land', href: '/muslim-resource-guide', color: '#2d7a47' },
      { id: 'ecological-memory', label: 'Ecological Memory', desc: 'Intergenerational observation chains', href: '/observation-chain-archive', color: '#D4A82A' },
      { id: 'urban-planning', label: 'Urban Planning Lens', desc: 'Ethical decisions in sustainable planning', href: '/muslim-resource-guide', color: '#2d8a87' },
    ],
    amanahCategories: [
      { id: 'land', label: 'Land Amanah', desc: 'Stewardship of the earth', weight: 4, color: '#c9a84c' },
      { id: 'knowledge', label: 'Ancestral Knowledge Amanah', desc: 'Preserving and transmitting ecological wisdom', weight: 3, color: '#D4A82A' },
      { id: 'planning', label: 'Planning Amanah', desc: 'Ethical urban and environmental decisions', weight: 2, color: '#2d8a87' },
    ],
    alhamdulilahWeights: [
      { category: 'Land Observation', weight: 4, desc: 'Documenting ecological knowledge' },
      { category: 'Knowledge Transmitted', weight: 5, desc: 'Sharing ancestral wisdom' },
      { category: 'Stewardship Act', weight: 7, desc: 'Direct land stewardship action' },
    ],
    primaryKey: 'Key 05: Amanah — The Trust',
    secondaryKey: 'Key 03: Taqwah — Consciousness',
    irlSequence: 'S10: Grassland Resilience (endurance and return)',
    mrgDomain: 'Environmental Stewardship',
  },
  'returning-citizen': {
    id: 'returning-citizen',
    label: 'Returning Citizen',
    arabic: 'عَائِد',
    icon: '⌘',
    lineageColor: '#5a5a8a',
    biomeColor: '#2d7a47',
    tagline: 'Mizan, restoration, and the Islah of community',
    modules: [
      { id: 'mizan-lens', label: 'Mizan Lens', desc: 'Restoration as the work of balance', href: '/inner-reality-lens', color: '#5a5a8a' },
      { id: 'islah-map', label: 'Islah Map', desc: 'Mapping restoration opportunities in your ward', href: '/muslim-resource-guide', color: '#2d7a47' },
      { id: 'reform-lens', label: 'Reform Lens', desc: 'Institutional reform as Amanah', href: '/muslim-resource-guide', color: '#D4A82A' },
      { id: 'reentry-bridge', label: 'Reentry Bridge', desc: 'Connecting returning citizens to resources', href: '/muslim-resource-guide', color: '#c9a84c' },
    ],
    amanahCategories: [
      { id: 'restoration', label: 'Restoration Amanah', desc: 'Mending what was broken', weight: 4, color: '#5a5a8a' },
      { id: 'community', label: 'Community Amanah', desc: 'Rebuilding community trust', weight: 3, color: '#2d7a47' },
      { id: 'reform', label: 'Reform Amanah', desc: 'Institutional and systemic change', weight: 3, color: '#D4A82A' },
    ],
    alhamdulilahWeights: [
      { category: 'Restoration Act', weight: 4, desc: 'One act of Islah in the community' },
      { category: 'Bridge Built', weight: 5, desc: 'Connecting someone to a resource' },
      { category: 'Reform Proposed', weight: 7, desc: 'Documented reform proposition' },
    ],
    primaryKey: 'Key 04: Mizan — The Balance',
    secondaryKey: 'Key 02: Haal — States of Being',
    irlSequence: 'S1: Seasonal Cycle (dormancy and return)',
    mrgDomain: 'Reentry & Restoration',
  },
};

// ─── ROLE AMANAH TRACKER ─────────────────────────────────────────────────────

function RoleAmanahTracker({ roleConfig }: { roleConfig: RoleConfig }) {
  const [entries, setEntries] = useState<Record<string, { count: number; notes: string[] }>>({});
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem(`haal_amanah_${roleConfig.id}`);
    if (saved) {
      try { setEntries(JSON.parse(saved)); } catch {}
    }
  }, [roleConfig.id]);

  const saveEntries = (e: typeof entries) => {
    setEntries(e);
    if (typeof window !== 'undefined') {
      localStorage.setItem(`haal_amanah_${roleConfig.id}`, JSON.stringify(e));
    }
  };

  const addEntry = (categoryId: string) => {
    const current = entries[categoryId] || { count: 0, notes: [] };
    const updated = {
      ...entries,
      [categoryId]: {
        count: current.count + 1,
        notes: newNote.trim() ? [...current.notes, newNote.trim()] : current.notes,
      },
    };
    saveEntries(updated);
    setNewNote('');
    setActiveCategory(null);
  };

  const totalWeight = roleConfig.amanahCategories.reduce((sum, cat) => {
    const count = entries[cat.id]?.count || 0;
    return sum + count * cat.weight;
  }, 0);

  return (
    <div className="rounded-xl p-5" style={{ background: `${roleConfig.lineageColor}06`, border: `1px solid ${roleConfig.lineageColor}20` }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: `${roleConfig.lineageColor}` }}>Amanah Tracker</p>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>Role-specific trust fulfillment</p>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-black" style={{ color: roleConfig.lineageColor }}>{totalWeight}</div>
          <div className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Amanah Weight</div>
        </div>
      </div>

      <div className="space-y-3">
        {roleConfig.amanahCategories.map(cat => {
          const count = entries[cat.id]?.count || 0;
          const notes = entries[cat.id]?.notes || [];
          const isActive = activeCategory === cat.id;

          return (
            <div key={cat.id} className="rounded-lg overflow-hidden" style={{ border: `1px solid ${cat.color}18` }}>
              <div
                className="flex items-center justify-between p-3 cursor-pointer"
                style={{ background: `${cat.color}06` }}
                onClick={() => setActiveCategory(isActive ? null : cat.id)}
              >
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest font-black" style={{ color: cat.color }}>{cat.label}</p>
                  <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10 }}>{cat.desc}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-mono text-lg font-black" style={{ color: cat.color }}>{count}</div>
                    <div className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.35)' }}>×{cat.weight} = {count * cat.weight}</div>
                  </div>
                  <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 10 }}>{isActive ? '▲' : '▼'}</span>
                </div>
              </div>

              {isActive && (
                <div className="p-3 border-t" style={{ borderColor: `${cat.color}15` }}>
                  {notes.length > 0 && (
                    <div className="mb-3 space-y-1">
                      {notes.slice(-3).map((note, i) => (
                        <p key={i} style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, fontStyle: 'italic' }}>"{note}"</p>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      placeholder="Optional note..."
                      className="flex-1 rounded px-3 py-1.5 font-mono text-xs outline-none"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#d4c5a9' }}
                      onKeyDown={e => e.key === 'Enter' && addEntry(cat.id)}
                    />
                    <button
                      onClick={() => addEntry(cat.id)}
                      className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
                      style={{ background: `${cat.color}12`, border: `1px solid ${cat.color}25`, color: cat.color }}
                    >
                      + Record
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ROLE ALHAMDULILLAH WEIGHTS ───────────────────────────────────────────────

function RoleAlhamdulilahWeights({ roleConfig }: { roleConfig: RoleConfig }) {
  return (
    <div className="rounded-xl p-5" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>Alhamdulillah Weight Categories</p>
      <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, marginBottom: 12 }}>
        Role-determined recognition weights — the ground state is already Alhamdulillah
      </p>
      <div className="space-y-3">
        {roleConfig.alhamdulilahWeights.map((w, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm font-black flex-shrink-0" style={{ background: `${roleConfig.lineageColor}15`, color: roleConfig.lineageColor }}>
              {w.weight}
            </div>
            <div className="flex-1">
              <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: '#d4c5a9' }}>{w.category}</p>
              <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10 }}>{w.desc}</p>
            </div>
            <div className="h-1.5 w-16 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full" style={{ width: `${(w.weight / 7) * 100}%`, background: roleConfig.lineageColor }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ROLE CONTEXT MODULES ─────────────────────────────────────────────────────

function RoleContextModules({ roleConfig }: { roleConfig: RoleConfig }) {
  return (
    <div>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.4)' }}>Role-Context Module Set</p>
      <div className="grid grid-cols-2 gap-3">
        {roleConfig.modules.map(mod => (
          <Link key={mod.id} href={mod.href}>
            <div className="rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02]" style={{ background: `${mod.color}08`, border: `1px solid ${mod.color}20` }}>
              <p className="font-mono text-[9px] uppercase tracking-widest font-black mb-1" style={{ color: mod.color }}>{mod.label}</p>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 10, lineHeight: 1.5 }}>{mod.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN ROLE GARDEN DASHBOARD ───────────────────────────────────────────────

export function RoleGardenDashboard() {
  const [selectedRole, setSelectedRole] = useState<SeedRole | null>(null);
  const [activeSection, setActiveSection] = useState<'modules' | 'amanah' | 'weights'>('modules');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('haal_caretaker_role') as SeedRole | null;
    if (saved && ROLE_CONFIGS[saved]) setSelectedRole(saved);
  }, []);

  const selectRole = (role: SeedRole) => {
    setSelectedRole(role);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_caretaker_role', role);
    }
  };

  const roleConfig = selectedRole ? ROLE_CONFIGS[selectedRole] : null;

  if (!selectedRole || !roleConfig) {
    return (
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(6,13,18,0.98)', border: '1px solid rgba(201,168,76,0.15)' }}>
        <div className="px-6 py-5 border-b" style={{ borderColor: 'rgba(201,168,76,0.1)' }}>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>◈ My Garden · Role Onboarding</p>
          <h2 className="font-mono text-xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Choose Your Role</h2>
          <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 12, marginTop: 2 }}>
            Your role determines your module set, Amanah trackers, and Alhamdulillah weight categories
          </p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.values(ROLE_CONFIGS).map(rc => (
              <div
                key={rc.id}
                onClick={() => selectRole(rc.id)}
                className="rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02]"
                style={{ background: `${rc.lineageColor}08`, border: `1px solid ${rc.lineageColor}20` }}
              >
                <div className="text-2xl mb-2" style={{ color: rc.lineageColor }}>{rc.icon}</div>
                <p className="font-mono text-[10px] uppercase tracking-widest font-black mb-1" style={{ color: rc.lineageColor }}>{rc.label}</p>
                <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10, lineHeight: 1.5 }}>{rc.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(6,13,18,0.98)', border: `1px solid ${roleConfig.lineageColor}25` }}>
      {/* Role header */}
      <div className="px-6 py-5 border-b" style={{ borderColor: `${roleConfig.lineageColor}15`, background: `${roleConfig.lineageColor}04` }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl" style={{ background: `${roleConfig.lineageColor}15`, color: roleConfig.lineageColor }}>
              {roleConfig.icon}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: `${roleConfig.lineageColor}` }}>◈ My Garden · {roleConfig.label}</p>
                <div className="w-2 h-2 rounded-full" style={{ background: roleConfig.lineageColor }} />
              </div>
              <h2 className="font-mono text-xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>{roleConfig.label} Path</h2>
              <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11 }}>{roleConfig.tagline}</p>
            </div>
          </div>
          <button
            onClick={() => setSelectedRole(null)}
            className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)' }}
          >
            Change Role
          </button>
        </div>

        {/* Role metadata */}
        <div className="flex gap-4 mt-4 flex-wrap">
          <div>
            <p className="font-mono text-[7px] uppercase tracking-widest mb-0.5" style={{ color: 'rgba(212,197,169,0.3)' }}>Primary Key</p>
            <p className="font-mono text-[9px]" style={{ color: '#D4A82A' }}>{roleConfig.primaryKey}</p>
          </div>
          <div>
            <p className="font-mono text-[7px] uppercase tracking-widest mb-0.5" style={{ color: 'rgba(212,197,169,0.3)' }}>IRL Entry</p>
            <p className="font-mono text-[9px]" style={{ color: '#2d8a87' }}>{roleConfig.irlSequence}</p>
          </div>
          <div>
            <p className="font-mono text-[7px] uppercase tracking-widest mb-0.5" style={{ color: 'rgba(212,197,169,0.3)' }}>MRG Domain</p>
            <p className="font-mono text-[9px]" style={{ color: '#2d7a47' }}>{roleConfig.mrgDomain}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Section tabs */}
        <div className="flex gap-2 mb-6">
          {(['modules', 'amanah', 'weights'] as const).map(s => (
            <button
              key={s}
              onClick={() => setActiveSection(s)}
              className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
              style={{
                background: activeSection === s ? `${roleConfig.lineageColor}12` : 'transparent',
                border: `1px solid ${activeSection === s ? `${roleConfig.lineageColor}35` : 'rgba(255,255,255,0.06)'}`,
                color: activeSection === s ? roleConfig.lineageColor : 'rgba(212,197,169,0.4)',
              }}
            >
              {s === 'modules' ? 'Modules' : s === 'amanah' ? 'Amanah Tracker' : 'Alhamdulillah Weights'}
            </button>
          ))}
        </div>

        {activeSection === 'modules' && <RoleContextModules roleConfig={roleConfig} />}
        {activeSection === 'amanah' && <RoleAmanahTracker roleConfig={roleConfig} />}
        {activeSection === 'weights' && <RoleAlhamdulilahWeights roleConfig={roleConfig} />}
      </div>
    </div>
  );
}

// ─── ROLE LINEAGE BADGE (for all tabs) ───────────────────────────────────────

export function RoleLineageBadge() {
  const [role, setRole] = useState<SeedRole | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('haal_caretaker_role') as SeedRole | null;
    if (saved && ROLE_CONFIGS[saved]) setRole(saved);
  }, []);

  if (!role) return null;

  const rc = ROLE_CONFIGS[role];
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: `${rc.lineageColor}10`, border: `1px solid ${rc.lineageColor}25` }}>
      <div className="w-2 h-2 rounded-full" style={{ background: rc.lineageColor }} />
      <span className="font-mono text-[8px] uppercase tracking-widest" style={{ color: rc.lineageColor }}>{rc.label}</span>
      <span style={{ color: rc.lineageColor, fontSize: 10 }}>{rc.icon}</span>
    </div>
  );
}

export { ROLE_CONFIGS };
export default RoleGardenDashboard;
